import { useState } from "react";

import { CountButton } from "@/Common/Components/Buttons/CountButton";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { Guid } from "@/Common/Models/Guid";
import { Patient } from "@/Common/Models/Patient/Patient";
import { MedicalItemsTab } from "@/Common/Models/Patient/PatientEnums";
import { AddFlowsheetModal } from "@/Flowsheets/Components/AddFlowsheetModal/AddFlowsheetModal";
import { CreateFlowsheetButton } from "@/Flowsheets/Components/CreateFlowsheetButton/CreateFlowsheetButton";
import { FlowsheetsList } from "@/Flowsheets/Components/FlowsheetsList/FlowsheetsList";
import { FlowsheetSummary } from "@/Flowsheets/Models/FlowsheetSummary";
import { FlowsheetTemplate } from "@/Flowsheets/Models/FlowsheetTemplate";
import { PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { MedicalItem } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemProblemAndCondition } from "@/Patient/Models/MedicalSummary/MedicalItemProblemAndCondition";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";

import styles from "./AssessmentSection.module.scss";

interface IAssessmentSectionProps {
    isEditMode: boolean;
    isReadOnly: boolean;
    noteModel: IPatientMedicalNoteModel;
    patientModel: Patient;
    toggleSectionOpen: () => void;
    addMedicalHistoryItem: (isFromAssessment: boolean) => void;
    createFlowsheet: (flowsheetTemplate: FlowsheetTemplate, patientId: Guid | string, encounterId: Guid) => void;
    onMedicalItemClick: (item: MedicalItem, view: MedicalItemsTab, fromAssessment: boolean) => void;
    onFlowsheetClick: (flowsheet: FlowsheetSummary) => void
    isSectionOpen: boolean;
}

function AssessmentSection(props: IAssessmentSectionProps): JSX.Element {
    const [isAddFlowsheetModalOpen, setIsAddFlowsheetModalOpen] = useState(false);

    const renderAssessments = (problemsToDisplay: MedicalItemProblemAndCondition[]): JSX.Element[] => {
        const result: JSX.Element[] = [];

        problemsToDisplay.forEach(problem => {
            let lifeStageRendered: boolean = false;
            let lifeStageHasBeenRendered: boolean = false;

            const invalidStyle: string = problem.DisplayAsInvalid ? "invalid-medical-item" : "";

            if (!isArrayNullOrUndefinedOrEmpty(problem.QualifiedDetails)) {
                problem.QualifiedDetails.forEach(qd => {
                    const mainRow: JSX.Element = (
                        <tr
                            key={"prob_main" + qd.Id}
                            className={"medical-note-item " + invalidStyle}
                            onClick={() => props.onMedicalItemClick(problem, MedicalItemsTab.MedicalProblem, true)}
                        >
                            <td className="c1">
                                <div className="tb-row tb">
                                    {!lifeStageHasBeenRendered && (
                                        <strong>
                                            <span className={invalidStyle}>
                                                {(!lifeStageRendered) ? PatientMedicalSummaryHelper.GetLifeStageString(problem) : ""}
                                            </span>
                                        </strong>
                                    )}
                                </div>
                            </td>
                            <td className="c2">
                                <div className="tb-row tb">
                                    <div className="add-btn-column" />
                                    {settingsStore.configuration.get("ShowDiagnosisCode") && (
                                        <a>{qd.Code} - </a>
                                    )}
                                    <a><span className={invalidStyle}>{qd.Text}</span></a>
                                </div>
                            </td>
                            <td className="c3">
                                <div className="tb-row tb">
                                    <a>{qd.StatusLookupId != null ? `(${cacheStore.GetLocalizedValueByLookupId(qd.StatusLookupId)})` : ""}</a>
                                </div>
                            </td>
                        </tr>
                    );

                    lifeStageHasBeenRendered = true;
                    result.push(mainRow);
                });
            }

            lifeStageRendered = true;

            const descriptionRow: JSX.Element = (
                <tr key={"prob_desc" + problem.MedicalItemId}>
                    <td className="c1 bottom-pad" />
                    <td className="c2 bottom-pad">
                        <div className="tb-row tb">
                            <div className="add-btn-column" />
                            <strong className="text-italic">
                                {JSXHelper.BreakStringIntoParagraphs(problem.ProblemDescription, true)}
                            </strong>
                        </div>
                    </td>
                    {!IsNullOrWhiteSpace(problem.ProblemStatusLookupId) && (
                        <td className="c3">
                            <div className="tb-row tb">
                                <a>{cacheStore.GetLocalizedValueByLookupId(problem.ProblemStatusLookupId)}</a>
                            </div>
                        </td>
                    )}
                </tr>
            );

            result.push(descriptionRow);

            if (!isArrayNullOrUndefinedOrEmpty(problem.QualifiedDetails))
                result.push(
                    <tr key={"empty" + problem.QualifiedDetails.firstOrDefault().Id}>
                        <td><span>&nbsp;</span></td>
                        <td> </td>
                        <td> </td>
                    </tr>
                );
        });

        return result;
    };

    const assessments = PatientMedicalSummaryHelper.GetMedicalProblemAssesmentForNote(
        props.noteModel.get("MedicalItemsProblemsAndDiagnostics").toArray(),
        props.noteModel,
        props.patientModel.IndividualResponse.DateOfBirth
    );

    const assessmentRenders = renderAssessments(assessments);

    function header(): JSX.Element {
        return (
            <div className="note-block-header" onClick={() => props.toggleSectionOpen()}>
                <div className="pull-right">
                    <span className={"glyphicon " + (props.isSectionOpen ? "glyphicon-chevron-up" : "glyphicon-chevron-down")} />
                </div>
                <h3>{cacheStore.GetString("ImpressionMLabel").toUpperCase()}</h3>
            </div>
        );
    }

    if (isArrayNullOrUndefinedOrEmpty(assessmentRenders) && props.noteModel.get("Encounter").IsSignedOff) {
        if (cacheStore.ClinicSettings.EnableChronicDiseaseManagement) {
            return (
                <div className="note-block medical-note-block">
                    {header()}
                    <div className="patient-note">
                        <table>
                            <tbody>
                                <tr>
                                    <td className="c1">
                                        <strong>
                                            {cacheStore.GetString("Flowsheets_FlowsheetsLabel")}
                                        </strong>
                                    </td>
                                    <td className="c2">
                                        <div className={styles.flowsheetRow}>
                                            <div className="add-btn-column" />
                                            <FlowsheetsList
                                                patientId={props.patientModel.IndividualResponse.Contact_id}
                                                encounterId={props.noteModel.get("Encounter").Id}
                                                onClick={flowsheet => props.onFlowsheetClick(flowsheet)}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        }
        else {
            return null;
        }
    }

    return (
        <>
            <div className="note-block medical-note-block">
                {header()}

                <div className={props.isReadOnly ? "no-hover" : ""}>
                    {props.isSectionOpen && (
                        <div className="patient-note">
                            <table>
                                <tbody>
                                    <tr>
                                        <td className="c1">
                                            <strong className="underline">
                                                {cacheStore.GetString("AddProblemDiagnosticLabel").replace("[+] ", "")}
                                            </strong>
                                        </td>
                                        <td className="c2">
                                            <div className="tb-row tb">
                                                {isArrayNullOrUndefinedOrEmpty(assessmentRenders) ? "--" : ""}
                                            </div>
                                        </td>
                                        <td className="c3" />
                                    </tr>

                                    {!isArrayNullOrUndefinedOrEmpty(assessmentRenders) &&
                                        assessmentRenders
                                    }

                                    {props.isEditMode && (
                                        <tr>
                                            <td className="c1" />
                                            <td className="c2">
                                                <div className="tb-row tb">
                                                    <div className="note-items-column">
                                                        <a onClick={() => props.addMedicalHistoryItem(true)}>
                                                            {cacheStore.GetString("AddProblemDiagnosticLabel")}
                                                        </a>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    {cacheStore.ClinicSettings.EnableChronicDiseaseManagement && (
                                        <>
                                            <tr>
                                                <td style={{ height: "10px" }} />
                                            </tr>
                                            <tr>
                                                <td className="c1">
                                                    <strong>
                                                        {cacheStore.GetString("Flowsheets_FlowsheetsLabel")}
                                                    </strong>
                                                </td>
                                                <td className="c2">
                                                    <div className={styles.flowsheetRow}>
                                                        <CreateFlowsheetButton
                                                            encounterId={props.noteModel.get("Encounter").Id}
                                                            patientId={props.noteModel.get("Encounter").PatientId as Guid}
                                                        >
                                                            <CountButton
                                                                count={0}
                                                                size="sm"
                                                                data-cy="medicalNote-add-flowsheet"
                                                                onClick={() => setIsAddFlowsheetModalOpen(true)}
                                                            />
                                                        </CreateFlowsheetButton>
                                                        <FlowsheetsList
                                                            patientId={props.patientModel.IndividualResponse.Contact_id}
                                                            encounterId={props.noteModel.get("Encounter").Id}
                                                            onClick={flowsheet => props.onFlowsheetClick(flowsheet)}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        </>

                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                {isAddFlowsheetModalOpen && cacheStore.ClinicSettings.EnableChronicDiseaseManagement && (
                    <AddFlowsheetModal
                        patientId={props.noteModel.get("Encounter").PatientId as Guid}
                        encounterId={props.noteModel.get("Encounter").Id}
                        onSubmit={(flowsheetTemplate: FlowsheetTemplate) => {
                            props.createFlowsheet(flowsheetTemplate, props.noteModel.get("Encounter").PatientId, props.noteModel.get("Encounter").Id);
                            setIsAddFlowsheetModalOpen(false);
                        }}
                        onClose={() => setIsAddFlowsheetModalOpen(false)}
                    />
                )}
            </div>
            <div className="padding-sm" />
        </>
    );
}

export { AssessmentSection };
